import {type Insets, Platform} from 'react-native'
import {type AppBskyActorDefs, BSKY_LABELER_DID} from '@atproto/api'

import {type ProxyHeaderValue} from '#/state/session/agent'
import {BLUESKY_PROXY_DID, CHAT_PROXY_DID, IS_DEV} from '#/env'

export const LOCAL_DEV_SERVICE =
  Platform.OS === 'android' ? 'http://10.0.2.2:2583' : 'http://localhost:2583'
export const STAGING_SERVICE = 'https://staging.bsky.dev'
export const BSKY_SERVICE = 'https://blacksky.app'
export const BSKY_SERVICE_DID = 'did:web:bsky.social'
export const PUBLIC_BSKY_SERVICE =
  (__DEV__ && process.env.EXPO_PUBLIC_APPVIEW_URL) ||
  'https://api.blacksky.community'
export const DEFAULT_SERVICE = BSKY_SERVICE

// Handle suffixes that identify a Latinsky-community account living on the shared
// Blacksky PDS. Explicit carveout — not a general resolution mechanism. (The
// Blacksky PDS host itself is derived from the default brand config; see
// resolveBrand.ts.)
export const LATINSKY_HANDLE_SUFFIXES = ['.latinsky.app', '.afrolatinsky.app']
export const LATINSKY_BRAND_SLUG = 'latinsky'

export const HELP_DESK_URL = `https://docs.blacksky.community/using-blacksky`
export const CHAT_SERVICE = 'https://api.bsky.chat'
export const EMBED_SERVICE = 'https://embed.bsky.app'
export const EMBED_SCRIPT = `${EMBED_SERVICE}/static/embed.js`
export const BSKY_DOWNLOAD_URL = 'https://blacksky.community/download'
export const ACCOUNT_MIGRATION_URL = 'https://move.blacksky.community'
export const STARTER_PACK_MAX_SIZE = 150
export const CARD_ASPECT_RATIO = 1200 / 630

// HACK
// Yes, this is exactly what it looks like. It's a hard-coded constant
// reflecting the number of new users in the last week. We don't have
// time to add a route to the servers for this so we're just going to hard
// code and update this number with each release until we can get the
// server route done.
// -prf
export const JOINED_THIS_WEEK = 560000 // estimate as of 12/18/24

export const DISCOVER_DEBUG_DIDS: Record<string, true> = {
  'did:plc:oisofpd7lj26yvgiivf3lxsi': true, // hailey.at
  'did:plc:p2cp5gopk7mgjegy6wadk3ep': true, // samuel.bsky.team
  'did:plc:ragtjsm2j2vknwkz3zp4oxrd': true, // pfrazee.com
  'did:plc:vpkhqolt662uhesyj6nxm7ys': true, // why.bsky.team
  'did:plc:3jpt2mvvsumj2r7eqk4gzzjz': true, // esb.lol
  'did:plc:vjug55kidv6sye7ykr5faxxn': true, // emilyliu.me
  'did:plc:tgqseeot47ymot4zro244fj3': true, // iwsmith.bsky.social
  'did:plc:2dzyut5lxna5ljiaasgeuffz': true, // darrin.bsky.team
}

const BASE_FEEDBACK_FORM_URL = `mailto:support@blacksky.app`
export function FEEDBACK_FORM_URL(_params: {
  email?: string
  handle?: string
}): string {
  return BASE_FEEDBACK_FORM_URL
}

export const MAX_DISPLAY_NAME = 64
export const MAX_DESCRIPTION = 256

export const MAX_GRAPHEME_LENGTH = 300

export const MAX_DRAFT_GRAPHEME_LENGTH = 1000

export const MAX_DM_GRAPHEME_LENGTH = 1000

export const MAX_GROUP_NAME_GRAPHEME_LENGTH = 50

// Recommended is 100 per: https://www.w3.org/WAI/GL/WCAG20/tests/test3.html
// but increasing limit per user feedback
export const MAX_ALT_TEXT = 2000

export const MAX_REPORT_REASON_GRAPHEME_LENGTH = 2000

export function IS_TEST_USER(handle?: string) {
  return handle && handle?.endsWith('.test')
}

export function IS_PROD_SERVICE(url?: string) {
  return url && url !== STAGING_SERVICE && !url.startsWith(LOCAL_DEV_SERVICE)
}

export const PROD_DEFAULT_FEED = (rkey: string) =>
  `at://did:plc:w4xbfzo7kqfes5zb7r6qv3rw/app.bsky.feed.generator/${rkey}`

export const STAGING_DEFAULT_FEED = (rkey: string) =>
  `at://did:plc:w4xbfzo7kqfes5zb7r6qv3rw/app.bsky.feed.generator/${rkey}`

export const PROD_FEEDS = [
  `feedgen|${PROD_DEFAULT_FEED('blacksky-trend')}`,
  `feedgen|${PROD_DEFAULT_FEED('blacksky-videos')}`,
]

export const STAGING_FEEDS = [
  `feedgen|${STAGING_DEFAULT_FEED('blacksky-trend')}`,
  `feedgen|${STAGING_DEFAULT_FEED('blacksky-videos')}`,
]

export const IMAGE_SIZE_CONFIG_POSTS = {
  maxDimension: 4000,
  maxSize: 2000000,
}

export const IMAGE_SIZE_CONFIG_2K_1MB = {
  maxDimension: 2000,
  maxSize: 1000000,
}

export const STAGING_LINK_META_PROXY =
  'https://cardyb.staging.bsky.dev/v1/extract?url='

export const PROD_LINK_META_PROXY = 'https://cardyb.bsky.app/v1/extract?url='

export function LINK_META_PROXY(_serviceUrl: string) {
  if (IS_DEV) {
    return STAGING_LINK_META_PROXY
  }

  return PROD_LINK_META_PROXY
}

// Hitslop constants
export const createHitslop = (size: number): Insets => ({
  top: size,
  left: size,
  bottom: size,
  right: size,
})
export const HITSLOP_10 = createHitslop(10)
export const HITSLOP_20 = createHitslop(20)
export const HITSLOP_30 = createHitslop(30)
export const LANG_DROPDOWN_HITSLOP = {top: 10, bottom: 10, left: 4, right: 4}
export const BACK_HITSLOP = HITSLOP_30
export const MAX_POST_LINES = 25

export const BSKY_APP_ACCOUNT_DID = 'did:plc:d2mkddsbmnrgr3domzg5qexf'
export const BLACKSKY_COMMUNITY_DID = 'did:plc:kta7dqcqoamo5ixlajxbtjps'
export const FOUNDER_DID = 'did:plc:w4xbfzo7kqfes5zb7r6qv3rw'

export const BSKY_FEED_OWNER_DIDS = [BSKY_APP_ACCOUNT_DID, FOUNDER_DID]

export const DISCOVER_FEED_URI =
  'at://did:plc:w4xbfzo7kqfes5zb7r6qv3rw/app.bsky.feed.generator/blacksky-trend'
export const VIDEO_FEED_URI =
  'at://did:plc:w4xbfzo7kqfes5zb7r6qv3rw/app.bsky.feed.generator/blacksky-videos'
export const FOR_YOU_FEED_URI =
  'at://did:plc:3guzzweuqraryl3rdkimjamk/app.bsky.feed.generator/for-you'
export const STAGING_VIDEO_FEED_URI =
  'at://did:plc:w4xbfzo7kqfes5zb7r6qv3rw/app.bsky.feed.generator/blacksky-videos'
export const VIDEO_FEED_URIS = [VIDEO_FEED_URI, STAGING_VIDEO_FEED_URI]
export const DISCOVER_SAVED_FEED = {
  type: 'feed',
  value: DISCOVER_FEED_URI,
  pinned: true,
}
export const TIMELINE_SAVED_FEED = {
  type: 'timeline',
  value: 'following',
  pinned: true,
}
export const VIDEO_SAVED_FEED = {
  type: 'feed',
  value: VIDEO_FEED_URI,
  pinned: true,
}

export const RECOMMENDED_SAVED_FEEDS: Pick<
  AppBskyActorDefs.SavedFeed,
  'type' | 'value' | 'pinned'
>[] = [DISCOVER_SAVED_FEED, TIMELINE_SAVED_FEED]

export const KNOWN_SHUTDOWN_FEEDS = [
  'at://did:plc:wqowuobffl66jv3kpsvo7ak4/app.bsky.feed.generator/the-algorithm', // for you by skygaze
]

export const GIF_SERVICE = 'https://gifs.bsky.app'

export const GIF_KLIPY_SEARCH = (params: string) =>
  `${GIF_SERVICE}/klipy/v2/search?${params}`
export const GIF_KLIPY_FEATURED = (params: string) =>
  `${GIF_SERVICE}/klipy/v2/featured?${params}`

export const GIF_GIPHY_SERVICE = 'https://gifs.blacksky.community'

export const GIF_GIPHY_SEARCH = (params: string) =>
  `${GIF_GIPHY_SERVICE}/v1/gifs/search?${params}`
export const GIF_GIPHY_TRENDING = (params: string) =>
  `${GIF_GIPHY_SERVICE}/v1/gifs/trending?${params}`

export const MAX_LABELERS = 20

export const VIDEO_SERVICE = 'https://video.blacksky.community'
export const VIDEO_SERVICE_DID = 'did:web:video.blacksky.community'

export const VIDEO_MAX_DURATION_MS = 60 * 60 * 1000 // 60 minutes in milliseconds
/**
 * Maximum size of a video in megabytes, _not_ mebibytes. Backend uses
 * ISO megabytes.
 */
export const VIDEO_MAX_SIZE_MB = 5000
export const VIDEO_MAX_SIZE = VIDEO_MAX_SIZE_MB * 1000 * 1000 // 5gb (Blacksky)

export const SUPPORTED_MIME_TYPES = [
  'video/mp4',
  'video/mpeg',
  'video/webm',
  'video/quicktime',
  'image/gif',
] as const

export type SupportedMimeTypes = (typeof SUPPORTED_MIME_TYPES)[number]

export const EMOJI_REACTION_LIMIT = 5

export const PUBLIC_BSKY_API = 'https://public.api.bsky.app'
export const PUBLIC_APPVIEW = 'https://api.blacksky.community'
export const PUBLIC_APPVIEW_DID = 'did:web:api.blacksky.community'
export const PUBLIC_STAGING_APPVIEW_DID = 'did:web:api.staging.bsky.dev'

export const APPVIEW_STATUS_URL = `${PUBLIC_APPVIEW}/status/indexer`

export const BLUESKY_FALLBACK_PROXY_DID = 'did:web:api.bsky.app'
export const BLUESKY_FALLBACK_PROXY_HEADER =
  `${BLUESKY_FALLBACK_PROXY_DID}#bsky_appview` as ProxyHeaderValue

// Immutable, unlike BLUESKY_PROXY_HEADER below — use for calls that must not
// follow that mutable header.
export const HOME_PROXY_HEADER =
  `${BLUESKY_PROXY_DID}#bsky_appview` as ProxyHeaderValue

export const HOME_APPVIEW_PINNED_OPTS = {
  headers: {'atproto-proxy': HOME_PROXY_HEADER},
}

export const BLUESKY_APPVIEW_PINNED_OPTS = {
  headers: {'atproto-proxy': BLUESKY_FALLBACK_PROXY_HEADER},
}

export const DEV_ENV_APPVIEW = `http://localhost:2584` // always the same

// temp hack for e2e - esb
export const BLUESKY_PROXY_HEADER = {
  value: `${BLUESKY_PROXY_DID}#bsky_appview`,
  get() {
    return this.value as ProxyHeaderValue
  },
  set(value: string) {
    this.value = value
  },
}

export const DM_SERVICE_HEADERS = {
  'atproto-proxy': `${CHAT_PROXY_DID}#bsky_chat`,
}

export const BLUESKY_MOD_SERVICE_HEADERS = {
  'atproto-proxy': `${BSKY_LABELER_DID}#atproto_labeler`,
}

export const BLUESKY_NOTIF_SERVICE_HEADERS = {
  'atproto-proxy': `${BLUESKY_PROXY_DID}#bsky_notif`,
}

export const webLinks = {
  tos: `https://www.blackskyweb.xyz/about/support/tos`,
  privacy: `https://www.blackskyweb.xyz/about/support/privacy-policy`,
  community: `https://www.blackskyweb.xyz/about/support/community-guidelines`,
  communityDeprecated: `https://www.blackskyweb.xyz/about/support/community-guidelines-deprecated`,
}
